const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8888';
const DEFAULT_STORE_ID = 1;

export interface MenuCategory {
  id: number;
  name: string;
  displayOrder: number;
}

export interface MenuItem {
  id: number;
  menuCategoryId: number;
  category: string;
  name: string;
  description: string | null;
  image: string | null;
  price: number;
  available: boolean;
}

export interface OrderItem {
  id: number;
  name: string;
  price: number;
  quantity: number;
}

export interface PosOrder {
  id: number;
  orderNumber: string;
  customerName: string;
  customerPhone: string;
  items: Array<{ name: string; quantity: number; price: number }>;
  total: number;
  status: string;
  timestamp: string;
  paymentStatus: string;
}

export interface MpesaCheckoutResponse {
  order: PosOrder;
  checkoutRequestId: string;
  merchantRequestId: string;
  customerMessage: string;
}

export interface MpesaCheckoutStatus {
  checkoutRequestId: string;
  merchantRequestId: string;
  orderId: number;
  orderNumber: string;
  paymentId: number;
  phoneNumber: string;
  amount: number;
  status: 'pending' | 'processing' | 'succeeded' | 'failed';
  orderStatus: string;
  paymentStatus: string;
  resultCode: number | null;
  resultDescription: string | null;
  mpesaReceipt: string | null;
  transactionDate: string | null;
  updatedAt: string;
  refreshError: string | null;
}

export interface ReportsResponse {
  summary: {
    totalRevenue: number;
    totalOrders: number;
    averageOrder: number;
  };
  orders: PosOrder[];
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers ?? {});
  const hasBody = init?.body !== undefined && init?.body !== null;

  if (hasBody && !(init?.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    headers,
    ...init,
  });

  const payload = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(payload.message ?? 'Request failed.');
  }

  return payload as T;
}

export async function fetchMenuCategories() {
  return apiFetch<{ categories: MenuCategory[] }>(
    `/api/menu/categories?storeId=${DEFAULT_STORE_ID}`
  );
}

export async function fetchMenuItems(includeUnavailable = false) {
  return apiFetch<{ items: MenuItem[] }>(
    `/api/menu/items?storeId=${DEFAULT_STORE_ID}&includeUnavailable=${includeUnavailable}`
  );
}

export async function createMenuItem(input: {
  menuCategoryId: number;
  itemName: string;
  description: string;
  imageUrl: string | null;
  basePrice: number;
  isAvailable: boolean;
}) {
  return apiFetch<{ item: MenuItem }>('/api/menu/items', {
    method: 'POST',
    body: JSON.stringify({
      ...input,
      storeId: DEFAULT_STORE_ID,
    }),
  });
}

export async function updateMenuItem(input: {
  menuItemId: number;
  menuCategoryId: number;
  itemName: string;
  description: string;
  imageUrl: string | null;
  basePrice: number;
  isAvailable: boolean;
}) {
  return apiFetch<{ item: MenuItem }>('/api/menu/items/update', {
    method: 'POST',
    body: JSON.stringify({
      ...input,
      storeId: DEFAULT_STORE_ID,
    }),
  });
}

export async function uploadMenuItemImage(file: File) {
  const formData = new FormData();
  formData.append('image', file);

  const response = await fetch(`${API_BASE_URL}/api/menu/items/upload-image`, {
    method: 'POST',
    body: formData,
  });

  const payload = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(payload.message ?? 'Image upload failed.');
  }

  return payload as { imageUrl: string };
}

export async function deleteMenuItem(menuItemId: number) {
  return apiFetch<{ message: string }>('/api/menu/items/delete', {
    method: 'POST',
    body: JSON.stringify({
      storeId: DEFAULT_STORE_ID,
      menuItemId,
    }),
  });
}

export async function updateMenuItemAvailability(menuItemId: number, isAvailable: boolean) {
  return apiFetch<{ item: MenuItem }>('/api/menu/items/availability', {
    method: 'POST',
    body: JSON.stringify({
      storeId: DEFAULT_STORE_ID,
      menuItemId,
      isAvailable,
    }),
  });
}

export async function createOrder(input: {
  customerName: string;
  customerPhone?: string;
  paymentMethod: 'cash' | 'card' | 'bank_transfer';
  cart: OrderItem[];
}) {
  return apiFetch<{ order: PosOrder }>('/api/orders', {
    method: 'POST',
    body: JSON.stringify({
      ...input,
      storeId: DEFAULT_STORE_ID,
    }),
  });
}

export async function startMpesaCheckout(input: {
  customerName: string;
  customerPhone: string;
  paymentMethod: 'mpesa';
  cart: OrderItem[];
}) {
  return apiFetch<MpesaCheckoutResponse>('/api/orders/checkout/mpesa', {
    method: 'POST',
    body: JSON.stringify({
      ...input,
      storeId: DEFAULT_STORE_ID,
    }),
  });
}

export async function fetchMpesaCheckoutStatus(checkoutRequestId: string, refresh = false) {
  const params = new URLSearchParams({
    storeId: String(DEFAULT_STORE_ID),
    checkoutRequestId,
  });

  if (refresh) {
    params.set('refresh', '1');
  }

  return apiFetch<MpesaCheckoutStatus>(`/api/orders/checkout/mpesa/status?${params.toString()}`);
}

export async function approveMpesaCheckoutManually(orderId: number) {
  return apiFetch<{ order: PosOrder }>('/api/orders/checkout/mpesa/manual-approve', {
    method: 'POST',
    body: JSON.stringify({
      storeId: DEFAULT_STORE_ID,
      orderId,
    }),
  });
}

export async function fetchLiveOrders() {
  return apiFetch<{ orders: PosOrder[] }>(`/api/orders/live?storeId=${DEFAULT_STORE_ID}`);
}

export async function setOrderStatus(orderId: number, status: string) {
  return apiFetch<{ order: PosOrder }>('/api/orders/status', {
    method: 'POST',
    body: JSON.stringify({
      storeId: DEFAULT_STORE_ID,
      orderId,
      status,
    }),
  });
}

export async function fetchReportOrders(range: string) {
  return apiFetch<ReportsResponse>(
    `/api/reports/orders?storeId=${DEFAULT_STORE_ID}&range=${range}`
  );
}
